export function getReferrer(): string {
  if (window.opener && window.opener.location) {
    return window.opener.location.toString()
  } else {
    return document.referrer
  }
}

export interface PostMessageData {
  metadata?: Record<string, any>
  mx: boolean
  type: string
  [key: string]: any
}

export function postMessageToParent(message: PostMessageData): boolean {
  const postUrl = getReferrer().replace(/([^:]+:\/\/[^\/]+).*/, '$1') // eslint-disable-line
  const otherWindow = window.parent || window.opener || false

  if (otherWindow && otherWindow.postMessage && postUrl) {
    otherWindow.postMessage(message, postUrl)
    return true
  }

  return false
}

export function postInsightOnTransaction(beatGuid: string): boolean {
  const message: PostMessageData = {
    metadata: { beat_guid: beatGuid },
    mx: true,
    type: 'mx/moneyDashboard/insightOnTransaction',
  }

  return postMessageToParent(message)
}

export function postClientConfiguredCta(template: string): boolean {
  const message: PostMessageData = {
    metadata: { template: template },
    mx: true,
    type: 'mx/pulse/client_configured_cta',
  }

  return postMessageToParent(message)
}

// ============================================================================
// Beat CTA post messages (ported from Pulse: src/utils/PostMessage.js +
// src/constants/PostMessage.js + src/epics/PostMessage.js).
//
// Scope: currently only the Save an extra $100 beat uses this. When a beat CTA
// has no drilldown drawer, clicking it notifies the host app via a beat/cta
// post message instead of opening a drawer. Native (mobile webview) hosts can't
// receive post messages, so we navigate the webview to a custom-scheme URL.
// ============================================================================

export const BEAT_CTA_EVENT = 'beat/cta'
export const BEAT_OPERATION_1 = 'op_1'
export const BEAT_OPERATION_2 = 'op_2'
export const BEAT_OPERATION_3 = 'op_3'

/** Minimal beat shape needed to build a CTA post message. */
export interface BeatCtaSource {
  guid: string
  template: string
  payload?: { action_string?: string } | null
}

export interface BeatCtaPayload {
  action: string
  beat_guid: string
  beat_template: string
  action_string?: string
  [key: string]: unknown
}

/**
 * Build the `[event, payload]` tuple for a beat CTA post message.
 * Mirrors Pulse's `getBaseCtaPostMessage`.
 */
export function getBaseCtaPostMessage(
  beat: BeatCtaSource,
  op: string = BEAT_OPERATION_1,
): [string, BeatCtaPayload] {
  const payload: BeatCtaPayload = {
    action: op,
    beat_guid: beat.guid,
    beat_template: beat.template,
  }

  if (beat?.payload?.action_string) {
    payload.action_string = beat.payload.action_string
  }

  return [BEAT_CTA_EVENT, payload]
}

/**
 * Send a beat post message to the parent window. Only posts when embedded in an
 * iframe (`window.self !== window.top`), mirroring Pulse's `sendPostMessage`
 * (default scheme `mx/pulse`). Returns false when not embedded or no target.
 */
export function sendBeatPostMessage(
  event: string,
  data: Record<string, unknown>,
  scheme = 'mx/pulse',
): boolean {
  if (window.self === window.top) {
    return false
  }

  const message: PostMessageData = {
    metadata: data,
    mx: true,
    type: `${scheme}/${event}`,
  }

  return postMessageToParent(message)
}

/**
 * Navigate a mobile webview to a custom-scheme URL instead of posting a message.
 * Mirrors Pulse's `setWebviewURL` (default scheme `mx`).
 */
export function setWebviewURL(event: string, data?: Record<string, unknown>, scheme = 'mx'): void {
  let webviewUrl = `${scheme}://pulse/${event}`

  if (data) {
    webviewUrl += '?metadata=' + encodeURIComponent(JSON.stringify(data))
  }

  window.location.href = webviewUrl
}

export interface SendBeatCtaOptions {
  /** True when running inside a native mobile webview. */
  isMobileWebview?: boolean
  /** Custom URL scheme for webview navigation (from client config). */
  webviewScheme?: string
  /** Current user guid, added to the metadata like the Pulse epic does. */
  userGuid?: string
  /** Operation string sent with the CTA (defaults to op_1). */
  op?: string
}

/**
 * Send a beat CTA to the host: builds the payload, adds `user_guid`, then either
 * posts a message to the host or navigates the webview. Mirrors the behaviour of
 * Pulse's `postMessages` epic (minus the RxJS buffering).
 */
export function sendBeatCta(
  beat: BeatCtaSource,
  { isMobileWebview, webviewScheme, userGuid, op }: SendBeatCtaOptions = {},
): void {
  const [event, basePayload] = getBaseCtaPostMessage(beat, op)
  const metadata: BeatCtaPayload = {
    ...basePayload,
    ...(userGuid ? { user_guid: userGuid } : {}),
  }

  if (isMobileWebview) {
    setWebviewURL(event, metadata, webviewScheme)
  } else {
    sendBeatPostMessage(event, metadata)
  }
}
